/* Mats · Columnas de colección
   - Barra de progreso sincronizada con el scroll (clic y arrastre)
   - Vídeos: solo se reproducen visibles y si no hay "reduce motion"
   - Compatible con el editor de temas (recarga de sección y selección de bloque) */
(() => {
  if (customElements.get('mats-collection-columns')) return;

  const INSET = 3; // px de margen interior del indicador
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MatsCollectionColumns extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mcc-track]');
      this.bar = this.querySelector('[data-mcc-bar]');
      this.thumb = this.querySelector('[data-mcc-thumb]');
      if (!this.track) return;

      this._raf = 0;
      this._onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      this._ro = new ResizeObserver(() => this.update());
      this._ro.observe(this.track);

      if (this.bar) {
        this._onPointerDown = this.onPointerDown.bind(this);
        this.bar.addEventListener('pointerdown', this._onPointerDown);
      }

      this.initVideos();

      this._onBlockSelect = (e) => {
        const card = e.target.closest('[data-mcc-card]');
        if (card && this.contains(card)) this.scrollToCard(card);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this._onScroll);
      this.bar?.removeEventListener('pointerdown', this._onPointerDown);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this._ro?.disconnect();
      this._io?.disconnect();
      reduceMotion.removeEventListener?.('change', this._onMotion);
      cancelAnimationFrame(this._raf);
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    metrics() {
      const barW = this.bar.clientWidth - INSET * 2;
      const ratio = this.track.clientWidth / this.track.scrollWidth;
      const thumbW = Math.max(40, Math.round(barW * ratio));
      return { barW, thumbW, room: Math.max(1, barW - thumbW) };
    }

    update() {
      if (!this.bar) return;
      const max = this.maxScroll;
      const scrollable = max > 2 && getComputedStyle(this.track).overflowX !== 'visible';
      this.bar.hidden = !scrollable;
      this.classList.toggle('is-scrollable', scrollable);
      if (!scrollable) return;

      const { thumbW, room } = this.metrics();
      const progress = Math.min(1, Math.max(0, Math.abs(this.track.scrollLeft) / max));
      this.thumb.style.setProperty('--mcc-thumb-w', `${thumbW}px`);
      this.thumb.style.setProperty('--mcc-thumb-x', `${progress * room}px`);
    }

    onPointerDown(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      const { thumbW, room } = this.metrics();
      const barRect = this.bar.getBoundingClientRect();
      const max = this.maxScroll;
      const factor = max / room;

      // Clic fuera del indicador: centrarlo bajo el puntero
      if (e.target !== this.thumb) {
        const x = e.clientX - barRect.left - INSET - thumbW / 2;
        this.track.scrollLeft = Math.min(max, Math.max(0, x * factor));
      }

      const startX = e.clientX;
      const startScroll = this.track.scrollLeft;
      this.classList.add('is-dragging');
      this.bar.setPointerCapture(e.pointerId);

      const move = (ev) => {
        this.track.scrollLeft = startScroll + (ev.clientX - startX) * factor;
      };
      const up = (ev) => {
        this.bar.releasePointerCapture?.(ev.pointerId);
        this.bar.removeEventListener('pointermove', move);
        this.bar.removeEventListener('pointerup', up);
        this.bar.removeEventListener('pointercancel', up);
        // Reactivar snap y ajustar a la tarjeta más cercana
        const left = this.track.scrollLeft;
        this.classList.remove('is-dragging');
        this.track.scrollLeft = left;
        this.snapToNearest();
      };
      this.bar.addEventListener('pointermove', move);
      this.bar.addEventListener('pointerup', up);
      this.bar.addEventListener('pointercancel', up);
    }

    snapToNearest() {
      const cards = [...this.track.querySelectorAll('[data-mcc-card]')];
      if (!cards.length) return;
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const trackLeft = this.track.getBoundingClientRect().left + pad;
      let best = cards[0];
      let bestD = Infinity;
      cards.forEach((c) => {
        const d = Math.abs(c.getBoundingClientRect().left - trackLeft);
        if (d < bestD) { bestD = d; best = c; }
      });
      this.scrollToCard(best);
    }

    scrollToCard(card) {
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const target =
        this.track.scrollLeft +
        (card.getBoundingClientRect().left - this.track.getBoundingClientRect().left) -
        pad;
      this.track.scrollTo({
        left: Math.min(this.maxScroll, Math.max(0, target)),
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    initVideos() {
      this.videos = [...this.querySelectorAll('video')];
      if (!this.videos.length) return;

      this.videos.forEach((v) => {
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.loop = true;
        v.removeAttribute('controls');
      });

      const sync = (v, visible) => {
        if (visible && !reduceMotion.matches) {
          if (v.preload === 'none') v.preload = 'auto';
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      };

      // display:none (vídeo de la otra versión) nunca intersecta → no descarga ni reproduce
      this._io = new IntersectionObserver(
        (entries) => entries.forEach((en) => sync(en.target, en.isIntersecting)),
        { rootMargin: '200px 0px' }
      );
      this.videos.forEach((v) => this._io.observe(v));

      this._onMotion = () => {
        this.videos.forEach((v) => {
          const r = v.getBoundingClientRect();
          const visible = r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
          sync(v, visible);
        });
      };
      reduceMotion.addEventListener?.('change', this._onMotion);
    }
  }

  customElements.define('mats-collection-columns', MatsCollectionColumns);
})();
